import React, { Component, useCallback, useEffect, useRef, useState } from "react";
import client from "@culturehq/client";
import { ActionButton, Pagination, Thumbnail } from "@culturehq/components";

import { useProfile } from "app/context";
import { useLiveEventCommentedListener } from "app/hooks";
import { t, Trans } from "app/utils";

import CommentForm from "components/comments/CommentForm";
import Comment from "components/comments/Comment";
import ErrorBoundary from "components/common/ErrorBoundary";

const createLiveComment = (createdComment, comments) => {
  if (!createdComment.parentCommentId) {
    return {
      comments: [createdComment, ...comments],
      success: t("Saved!")
    };
  }

  return {
    comments: comments.map(comment => {
      if (comment.id !== createdComment.parentCommentId) {
        return comment;
      }

      return {
        ...comment,
        childComments: comment.childComments.concat(createdComment)
      };
    })
  };
};

const deleteLiveComment = (deletedComment, comments) => {
  const filter = comment => comment.id !== deletedComment.id;

  return comments.filter(filter).map(comment => ({
    ...comment,
    childComments: comment.childComments.filter(filter)
  }));
};

const toggleLiveCommentCheer = (commentId, cheerForUser, cheersCount, comments, live = false) => (
  comments.map(comment => {
    if (comment.id === commentId) {
      if (live) {
        return { ...comment, cheersCount };
      }
      return { ...comment, cheerForUser, cheersCount };
    }

    return {
      ...comment,
      childComments: comment.childComments.map(child => (
        child.id === commentId ? { ...child, cheerForUser, cheersCount } : child
      ))
    };
  })
);

const createComment = createdComment => ({ comments }) => {
  if (!createdComment.parentCommentId) {
    return {
      comments: [createdComment, ...comments],
      success: t("Saved!")
    };
  }

  return {
    comments: comments.map(comment => {
      if (comment.id !== createdComment.parentCommentId) {
        return comment;
      }

      return {
        ...comment,
        childComments: comment.childComments.concat(createdComment)
      };
    })
  };
};

const deleteComment = deletedComment => {
  const filter = comment => comment.id !== deletedComment.id;

  return ({ comments }) => ({
    comments: comments.filter(filter).map(comment => ({
      ...comment,
      childComments: comment.childComments.filter(filter)
    }))
  });
};

const toggleCommentCheer = (commentId, cheerForUser, cheersCount) => ({ comments }) => ({
  comments: comments.map(comment => {
    if (comment.id === commentId) {
      return { ...comment, cheerForUser, cheersCount };
    }

    return {
      ...comment,
      childComments: comment.childComments.map(child => (
        child.id === commentId ? { ...child, cheerForUser, cheersCount } : child
      ))
    };
  })
});

class CommentsContext extends Component {
  static defaultProps = {
    onChange: () => {}
  };

  constructor(props) {
    super(props);

    this.state = { comments: [], success: false };
  }

  componentDidMount() {
    this.componentIsMounted = true;
    this.fetchComments(1);
  }

  componentDidUpdate(prevProps, prevState) {
    const { onChange } = this.props;
    const { comments } = this.state;

    if (comments.length !== prevState.comments.length) {
      onChange(comments.length);
    }
  }

  componentWillUnmount() {
    this.componentIsMounted = false;
  }

  fetchComments = async page => {
    const { options, listCall } = this.props;
    const { comments, pagination } = await listCall({ ...options, page });

    if (this.componentIsMounted) {
      this.setState({ comments, pagination });
    }
  };

  handleSubmit = async values => {
    const { options, createCall } = this.props;

    this.setState({ success: false });

    const { comment } = await createCall({ ...options, ...values });
    this.setState(createComment(comment));

    if (!comment.parentCommentId) {
      setTimeout(() => this.setState({ success: false }), 2000);
    }
  };

  handleDelete = async deletedComment => {
    try {
      await client.deleteComment({ commentId: deletedComment.id });

      this.setState(deleteComment(deletedComment));
    } catch (error) {
      if (error.status !== 404) {
        throw error;
      }
    }
  };

  handleCheerToggle = (commentId, cheerForUser, cheersCount) => {
    this.setState(toggleCommentCheer(commentId, cheerForUser, cheersCount));
  };

  render() {
    const { onToggleShowMore, profile, showAvatar, showMore } = this.props;
    const { success, comments, pagination } = this.state;

    return (
      <div className="comments row clearfix">
        <div className="col-sm-12">
          <ErrorBoundary>
            {showAvatar && (
              <div className="comments--avatar">
                <Thumbnail image={profile.avatar.thumbUrl} />
              </div>
            )}
            <CommentForm onSubmit={this.handleSubmit} success={success} />

            {(showMore ? comments : comments.slice(0, 3)).map(comment => (
              <Comment
                key={comment.id}
                comment={comment}
                onSubmit={this.handleSubmit}
                onDelete={this.handleDelete}
                onCheerToggle={this.handleCheerToggle}
              />
            ))}

            {showMore && <Pagination {...pagination} onClick={this.fetchComments} />}

            {!showMore && comments.length > 3 && (
              <div className="text-center">
                <ActionButton
                  className="comments--show-more"
                  icon="ios-chatbubble-outline"
                  onClick={onToggleShowMore}
                >
                  <Trans>Show More Comments</Trans>
                </ActionButton>
              </div>
            )}
          </ErrorBoundary>
        </div>
      </div>
    );
  }
}

const LiveComments = ({
  createCall,
  listCall,
  showAvatar,
  onChange = () => {},
  options,
  profile,
  showMore,
  onToggleShowMore
}) => {
  const [comments, setComments] = useState([]);
  const [pagination, setPagination] = useState([]);
  const [success, setSuccess] = useState(false);

  useLiveEventCommentedListener(
    useCallback(
      ({ cheer, comment, action }) => {
        const { user } = comment;
        if ((action === "new" && profile.id === user.id)
          || ((action === "new_cheer" || action === "remove_cheer")
          && profile.id === cheer.user.id)) {
          return;
        }

        switch (action) {
          case "new":
            setComments(createLiveComment(comment, comments).comments);
            break;
          case "remove":
            setComments(deleteLiveComment(comment, comments));
            break;
          case "new_cheer":
            setComments(toggleLiveCommentCheer(
              comment.id, null, comment.cheersCount + 1, comments, true)
            );
            break;
          case "remove_cheer":
            setComments(toggleLiveCommentCheer(
              comment.id, null, comment.cheersCount, comments, true)
            );
            break;
          default:
            break;
        }
      },
      [comments, profile]
    )
  );

  useEffect(
    () => {
      const getComments = async page => {
        const { comments: results, pagination: pages } = await listCall({ ...options, page });
        setComments(results);
        setPagination(pages);
      };
      getComments(1);
    },
    [listCall, options]
  );

  const prevCommentsRef = useRef();
  useEffect(() => {
    prevCommentsRef.current = comments;
  });
  const prevComments = prevCommentsRef.current || [];
  if (comments.length !== prevComments.length) {
    onChange(comments.length);
  }

  const fetchComments = async page => {
    const { comments: results, pagination: pages } = await listCall({ ...options, page });
    setComments(results);
    setPagination(pages);
  };

  const handleSubmit = async values => {
    setSuccess(false);

    const { comment } = await createCall({ ...options, ...values });
    const { comments: results, success: message } = createLiveComment(comment, comments);
    setComments(results);
    setSuccess(message);

    if (!comment.parentCommentId) {
      setTimeout(() => setSuccess(false), 2000);
    }
  };

  const handleDelete = async deletedComment => {
    try {
      await client.deleteComment({ commentId: deletedComment.id });
      setComments(deleteLiveComment(deletedComment, comments));
    } catch (error) {
      if (error.status !== 404) {
        throw error;
      }
    }
  };

  const handleCheerToggle = (commentId, cheerForUser, cheersCount) => {
    setComments(toggleLiveCommentCheer(commentId, cheerForUser, cheersCount, comments));
  };

  return (
    <div className="comments row clearfix">
      <div className="col-sm-12">
        <ErrorBoundary>
          {showAvatar && (
            <div className="comments--avatar">
              <Thumbnail image={profile.avatar.thumbUrl} />
            </div>
          )}
          <CommentForm onSubmit={handleSubmit} success={success} />

          {(showMore ? comments : comments.slice(0, 3)).map(comment => (
            <Comment
              key={comment.id}
              comment={comment}
              onSubmit={handleSubmit}
              onDelete={handleDelete}
              onCheerToggle={handleCheerToggle}
            />
          ))}

          {showMore && <Pagination {...pagination} onClick={fetchComments} />}

          {!showMore && comments.length > 3 && (
            <div className="text-center">
              <ActionButton
                className="comments--show-more"
                icon="ios-chatbubble-outline"
                onClick={onToggleShowMore}
              >
                <Trans>Show More Comments</Trans>
              </ActionButton>
            </div>
          )}
        </ErrorBoundary>
      </div>
    </div>
  );
};

const Comments = ({
  createCall,
  listCall,
  liveComments = false,
  onChange,
  options,
  showAvatar = true,
  showMore: initialShowMore = false
}) => {
  const { profile } = useProfile();

  const [showMore, setShowMore] = useState(initialShowMore);
  const handleToggleShowMore = () => setShowMore(value => !value);

  if (liveComments) {
    return (
      <LiveComments
        createCall={createCall}
        listCall={listCall}
        showAvatar={showAvatar}
        onChange={onChange}
        options={options}
        profile={profile}
        showMore={showMore}
        onToggleShowMore={handleToggleShowMore}
      />
    );
  }

  return (
    <CommentsContext
      createCall={createCall}
      listCall={listCall}
      showAvatar={showAvatar}
      onChange={onChange}
      options={options}
      profile={profile}
      showMore={showMore}
      onToggleShowMore={handleToggleShowMore}
    />
  );
};

export const EventComments = ({ event, onChange, liveComments = false }) => (
  <Comments
    createCall={client.createEventComment}
    listCall={client.listEventComments}
    onChange={onChange}
    options={{ eventId: event.id }}
    liveComments={liveComments}
    showMore
  />
);

export const PhotoComments = ({ photo, onChange }) => (
  <Comments
    createCall={client.createPhotoComment}
    listCall={client.listPhotoComments}
    onChange={onChange}
    options={{ photoId: photo.id }}
    showMore
  />
);

export const RecognitionComments = ({ recognition, onChange }) => (
  <Comments
    createCall={client.createRecognitionComment}
    listCall={client.listRecognitionComments}
    onChange={onChange}
    options={{ recognitionId: recognition.id }}
  />
);

export const UserActivityComments = ({ userActivity, onChange }) => (
  <Comments
    createCall={client.createUserActivityComment}
    listCall={client.listUserActivityComments}
    onChange={onChange}
    options={{ userActivityId: userActivity.id }}
  />
);

export const FeedItemComments = ({ item, onChange }) => {
  const { activityType, entity } = item;

  switch (activityType) {
    case "event_created":
      return <EventComments event={entity} onChange={onChange} />;
    case "photo_created":
      return <PhotoComments photo={entity} onChange={onChange} />;
    case "recognition_created":
      return <RecognitionComments recognition={entity} onChange={onChange} />;
    default:
      return <UserActivityComments userActivity={item} onChange={onChange} />;
  }
};
